import { useCallback, useState, useEffect } from "react";
import { useFormik } from "formik";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

// Components
import { Button, ServiceTypeFormCard, Header, Body } from "../../../../components/UI";
import { Heading2 } from "../../../../components/typography";

// Form Models
import { BranchServiceTypeFormModel } from "../../../../models/app/forms";

// Calls
import {
  fetchBranchServiceTypes,
  updateBranchServiceTypes
} from "../../../../api/calls/general-branch-settings.call";

// Builders
import * as buttonDesignType from "../../../../builders/buttonDesign.types";

// Validations
import { branchServiceTypeValidation } from "../../../../services/validations/service-type-and-capacity.validation";

// Styles
import { ServiceTypeAndCapacityFormStyles } from "./serviceTypeAndCapacityForm.styles";

const ServiceTypeAndCapacityForm = ({ branchId }) => {
  const { systemComponents } = useSelector(state => state.app);
  const [initialValues, setInitialValue] = useState([]);

  const getBranchServiceTypes = useCallback(async () => {
    const response = await fetchBranchServiceTypes(branchId);
    if (response.hasError) {
      toast.error(response.error.message);
      return;
    }
    setInitialValue(
      response.branchServiceTypes.map(
        branchServiceType => new BranchServiceTypeFormModel(branchServiceType)
      )
    );
  }, [branchId]);

  useEffect(() => {
    getBranchServiceTypes();
  }, [getBranchServiceTypes]);

  const updateServiceTypesHandler = async values => {
    const response = await updateBranchServiceTypes(
      { branchServiceTypes: values },
      branchId
    );
    if (response.hasError) {
      toast.error(response.error.message);
      return;
    }
    toast.success("Branch service types successfully updated.");
    setInitialValue(
      response.branchServiceTypes.map(
        branchServiceType => new BranchServiceTypeFormModel(branchServiceType)
      )
    );
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValues,
    onSubmit: updateServiceTypesHandler,
    validate: branchServiceTypeValidation
  });

  const { values, errors, handleSubmit, setValues, dirty } = formik;

  const handleToggle = useCallback(
    serviceType => {
      const existing = values.find(v => v.id === serviceType.id);
      if (existing) {
        setValues(prevValues =>
          prevValues.filter(v => v.id !== serviceType.id)
        );
      } else {
        setValues(prevValues => [
          ...prevValues,
          new BranchServiceTypeFormModel({
            id: serviceType.id,
            capacity: 1,
            mobility: serviceType.mobility
          })
        ]);
      }
    },
    [values, setValues]
  );

  /**
   * Set capacity of branch service type
   * @param e - input element
   * @param id
   * @returns void
   */
  const handleCapactyChange = useCallback(
    (e, id) => {
      const handleNumbersChangeOnly = previousValue => {
        let inputValue = e.target.validity.valid
          ? parseInt(e.target.value, 0)
          : previousValue;
        if (isNaN(inputValue)) {
          inputValue = "";
        }

        return inputValue;
      };

      setValues(prevValues =>
        prevValues.map(serviceType =>
          serviceType.id === id
            ? new BranchServiceTypeFormModel({
                ...serviceType,
                capacity: handleNumbersChangeOnly(serviceType.capacity)
              })
            : serviceType
        )
      );
    },
    [setValues]
  );

  const onSubmit = e => {
    e.preventDefault();
    if (errors.values) {
      toast.error(errors.values);
      return;
    }
    handleSubmit();
  };

  return (
    <ServiceTypeAndCapacityFormStyles>
      <Header>
        <div className="cardHeaderRow">
          <Heading2>SERVICE TYPE AND CAPACITY</Heading2>
          {dirty && (
            <Button
              className="sectionSaveBtn"
              onClick={onSubmit}
              buttonDesignType={buttonDesignType.PRIMARY}
            >
              Save Changes
            </Button>
          )}
        </div>
      </Header>
      <Body>
        <form onSubmit={onSubmit} className="form">
          <div className="serviceTypesContainer">
            {systemComponents?.serviceTypes?.map(serviceType => (
              <ServiceTypeFormCard
                key={serviceType.id}
                serviceType={serviceType}
                value={values.find(type => type.id === serviceType.id)}
                handleCapactyChange={handleCapactyChange}
                onToggle={handleToggle}
              />
            ))}
          </div>
        </form>
      </Body>
    </ServiceTypeAndCapacityFormStyles>
  );
};

export default ServiceTypeAndCapacityForm;
